<script setup>
  import {
    ref,
    computed,
    watchPostEffect,
    toRaw,
  } from 'vue';

  import axios from '@js/clients/axios';
  import MESSAGE from '@js/messages';

  import monetize from '@formatters/monetize';

  import filled from '@/components/Form/javascripts/filled';

  import CredereButton from '@/components/Credere/Button.vue';
  import CredereAlert from '@/components/Credere/Alert.vue';
  import FormCore from '@/components/Form/Core.vue';

  import CALLBACKS from './showroom/javascripts/callbacks';
  import COMBINE from './showroom/javascripts/combine';
  import CONSTANTS from './showroom/javascripts/constants';
  import SIZES from './showroom/javascripts/sizes';
  import UNGROUP from './showroom/javascripts/ungroup';
  import SORTS from './showroom/javascripts/sorts';
  import INLINES from './showroom/javascripts/inlines';

  import parser from './showroom/javascripts/parser';
  import normalizer from './showroom/javascripts/normalizer';

  import SCHEMA from './showroom/SCHEMA';

  const _PLACEHOLDER = [{
    id: 'sample1',
    price_cents: 999999999,
    vehicle_model: { name: 'NOME', version: 'VERSÃO' },
  }, {
    id: 'sample2',
    price_cents: 999999,
    vehicle_model: { name: 'NOME DOIS', version: 'VERSÃO DOIS' },
  }, {
    id: 'sample3',
    price_cents: 9999999999,
    vehicle_model: { name: 'NOME', version: 'VERSÃO TRÊS' },
  }, {
    id: 'sample4',
    price_cents: 999,
    vehicle_model: { name: 'NOME MAIOR', version: 'VERSÃO' },
  }];

  const refCore = ref();

  const globalSettings = window.CREDERE.settings || {};

  const SETTINGS = globalSettings.vehicles?.showroom || {};
  const STORE_ID = SETTINGS.store?.id || SETTINGS.store_id;

  const domains = ref({});

  if (!filled(SETTINGS.zero_km)) {
    SETTINGS.zero_km = true;
  }

  const zeroKM = ref(![false, 'false'].includes(SETTINGS.zero_km));
  watchPostEffect(() => {
    zeroKM.value = [true, 'true'].includes(refCore.value?.planter.values.value['vehicles[zero_km]']);
  });

  const entity = ref({
    vehicles: {
      store_id: STORE_ID,

      q: SETTINGS.q,
      sort: SETTINGS.sort || SCHEMA.vehicles.fields.sort.options[0].value,

      vehicle_brand_id: SETTINGS.vehicle_brand_id,
      vehicle_type_id: SETTINGS.vehicle_type_id,
      zero_km: zeroKM.value,
    },
  });

  const singular = computed(() => (zeroKM.value ? 'vehicle_model' : 'vehicle'));
  const plural = computed(() => (zeroKM.value ? 'vehicle_models' : 'vehicles'));
  const endpoint = computed(() => `${plural.value}/showroom`);

  const replacing = ref(true);
  const vehicles = ref(_PLACEHOLDER);

  const ASPECT_X = 7;
  const ASPECT_Y = 5;
  const ASPECT_RATIO_STYLE = { '--ar': `${ASPECT_X} / ${ASPECT_Y}` };

  function schemaInitializer() {
    return SCHEMA;
  }

  function vehiclesSuccess(response) {
    const pricing = zeroKM.value ? 'public_price_cents' : 'price_cents';

    let sorting = refCore.value?.planter.values.value['vehicles[sort]'];
    sorting = zeroKM.value ? sorting : sorting.replace(/^public_/, '');

    vehicles.value = (response?.data?.[zeroKM.value ? 'vehicle_models' : 'vehicles'] || [])
      .sort((a, b) => (/_asc/.test(sorting) ? a : b)[pricing] - (/_asc/.test(sorting) ? b : a)[pricing]);
  }

  function vehiclesFailure() {
    vehicles.value = [];
  }

  function preloadSuccess(responses) {
    domains.value.vehicle_brand = responses[0]?.data?.vehicle_brands;
    domains.value.vehicle_type = responses[1]?.data?.vehicle_types;

    setTimeout(() => refCore.value.force(), 10);
  }

  function preloadFailure(error) {
    /* eslint-disable no-console */
    console.log('VEHICLES/SHOWROOM FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    // eslint-disable-next-line no-alert
    window.alert('Ocorreu um erro ao tentar carregar as configurações do formulário. Por favor, tente novamente.');
  }

  function force() {
    MESSAGE.VEHICLE_SHOWROOM_ACTION({
      action: 'showroom/filter',
      settings: {
        q: refCore.value?.planter.values.value['vehicles[q]'],
        vehicle_brand_id: refCore.value?.planter.values.value['vehicles[vehicle_brand_id]'],
        vehicle_type_id: refCore.value?.planter.values.value['vehicles[vehicle_type_id]'],
        zero_km: refCore.value?.planter.values.value['vehicles[zero_km]'],
        sort: refCore.value?.planter.values.value['vehicles[sort]'],
      },
    });

    replacing.value = true;

    if (vehicles.value !== _PLACEHOLDER) {
      vehicles.value = _PLACEHOLDER;
    }
  }

  function success(hierarchy, action, index, parsed, response) {
    replacing.value = false;

    vehiclesSuccess(response);
  }

  function failure(hierarchy, action, index, parsed, error) {
    replacing.value = false;

    // eslint-disable-next-line no-console
    console.error('VEHICLES/LIST', error);

    vehiclesFailure(error);
  }

  function chain(/* hierarchy, action, index, parsed, chain */) {}

  function centless(value = 0) {
    return monetize(value, { prefix: '' }).split(',').shift();
  }

  const useds = computed(() => vehicles.value.map((v) => v.object_type === 'Vehicle'));
  const identifiers = computed(() => vehicles.value.map((v, i) => `${useds.value[i] ? v.public_api_identifier : v.id}`));

  function _version(vehicle) {
    return vehicle.vehicle_model?.version || vehicle.version;
  }

  function _name(vehicle) {
    return vehicle.vehicle_model?.name || vehicle.name;
  }

  function _brand(vehicle) {
    return vehicle.vehicle_model?.vehicle_brand?.name || vehicle.vehicle_model?.brand || vehicle.vehicle_brand?.name || vehicle.brand || '';
  }

  const titles = computed(() => vehicles.value.map((vehicle) => {
    const [version, subversion] = _version(vehicle).split(/ - /);

    return {
      brand: _brand(vehicle) || undefined,
      name: _name(vehicle),
      version,
      subversion,
    };
  }));

  const grouped = computed(() => titles.value.reduce((r, key) => {
    const {
      brand,
      name,
      version,
      subversion,
    } = key;

    r[brand] = r[brand] || {};
    r[brand][name] = r[brand][name] || {};
    r[brand][name][version] = r[brand][name][version] || {};
    r[brand][name][version][subversion] = r[brand][name][version][subversion] || 0;
    r[brand][name][version][subversion] += 1;

    return r;
  }, {}));

  const items = computed(() => titles.value.map((key, index) => {
    const vehicle = vehicles.value[index];
    const { brand, subversion } = key;
    let { name, version } = key;

    const versions = Object.keys(grouped.value[brand][name]).length > 1;
    const subversions = Object.keys(grouped.value[brand][name][version]).length > 1;

    if (versions) {
      name += ` - ${version}`;

      if (subversions) {
        name += ` - ${subversion}`;
      }
    }

    if (subversions) {
      version += ` - ${subversion}`;
    }

    return {
      id: vehicle.id,
      brand: domains.value.vehicle_brand?.length !== 1 ? brand : null,
      name,
      version,
      price: centless(vehicle.public_price_cents || vehicle.price_cents),
      years: useds.value[index] ? `${vehicle.manufacture_year} / ${vehicle.model_year} (${centless((vehicle.km_mileage || 0) * 100)}km)` : 'Novo (0km)',
      picture: vehicle.pictures?.length
        ? (vehicle.pictures.find((picture) => picture.main) || vehicle.pictures[0]).file_url
        : vehicle.public_picture?.url,
    };
  }));

  const UNDEFINED_URL = '&undefined_url=true';
  const urls = computed(() => vehicles.value.map((v, index) => {
    const key = useds.value[index] ? 'vehicle' : 'vehicle_model';

    const vehicleID = identifiers.value[index];
    const storeID = SETTINGS.store_public_api_identifier;
    const sellerID = SETTINGS.seller_public_identifier;

    if (SETTINGS.url?.[key]) {
      return SETTINGS.url[key].replace(':identifier', vehicleID);
    }

    let url = `?${key}_id=${vehicleID}`;

    if (storeID) {
      url += `&store_id=${storeID}`;
    }

    if (sellerID) {
      url += `&seller_id=${sellerID}`;
    }

    return `${url}${UNDEFINED_URL}`;
  }));

  const is = computed(() => urls.value.map((i) => (i.match(UNDEFINED_URL) ? 'span' : 'a')));

  function click(e, index) {
    const vehicle = vehicles.value[index];
    const key = useds.value[index] ? 'vehicle' : 'vehicle_model';

    MESSAGE.VEHICLE_SHOWROOM_ACTION({
      action: 'showroom/click',
      settings: {
        [key]: toRaw(vehicle),
        url: urls.value[index],
        _blank: !!e.altKey || !!e.metaKey || !!e.shiftKey,
      },
    });
  }

  const _finished = ref({});
  function finished(index) {
    _finished.value[identifiers.value[index]] = true;
  }

  const classes = computed(() => vehicles.value.map((vehicle, index) => ({
    'credere-vehicles-showroom-item': true,

    'state--loading': !_finished.value[identifiers.value[index]],
    'state--replacing': replacing.value,
  })));

  if (!STORE_ID) {
    // eslint-disable-next-line no-alert
    window.alert('Não foi possível carregar a listagem com os dados informados. Por favor, verifique a compatibilidade entre os seus dados e tente novamente.');
  } else {
    Promise
      .all([
        axios.get('/vehicle_brands', { params: { store_id: SETTINGS.store_id } }),
        axios.get('/vehicle_types', { params: { store_id: SETTINGS.store_id } }),
      ])
      .then(preloadSuccess)
      .catch(preloadFailure);
  }
</script>

<template>
  <section class="credere-vehicles-showroom">
    <form-core
      ref="refCore"

      class="credere-vehicles-showroom-form"

      :initializer="schemaInitializer"
      :entity="entity"

      :settings="SETTINGS"

      :callbacks="CALLBACKS"
      :combine="COMBINE"
      :constants="CONSTANTS"
      :sizes="SIZES"
      :ungroup="UNGROUP"
      :sorts="SORTS"
      :inlines="INLINES"

      :domains="domains"

      :parser="parser"
      :normalizer="normalizer"

      :endpoint="endpoint"
      :singular="singular"
      :plural="plural"
      :data="Object.keys(SCHEMA)[0]"

      :variants="{ section: 'A' }"

      action="list"

      @force="force"

      @success="success"
      @failure="failure"
      @chain="chain"
    />

    <ul
      class="credere-vehicles-showroom-list"
      :style="ASPECT_RATIO_STYLE"
    >
      <li
        v-for="(item, index) in items"
        :key="item.id"
        :class="classes[index]"
      >
        <component
          :is="is[index]"
          :href="urls[index]"
          class="credere-vehicles-showroom-link"
          _target="blank"
          @click.exact.stop.prevent="e => click(e, index)"
        >
          <span class="credere-vehicles-showroom-top">
            <span class="credere-vehicles-showroom-frame">
              <img
                loading="lazy"
                :src="item.picture"
                class="credere-vehicles-showroom-image"
                @load="() => finished(index)"
                @error="() => finished(index)"
              >
            </span>

            <span class="credere-vehicles-showroom-info">
              <h3 class="credere-vehicles-showroom-name">
                {{ item.brand }}
                {{ item.name }}
              </h3>

              <h4 class="credere-vehicles-showroom-price">
                {{ item.price }}
              </h4>

              <p class="credere-vehicles-showroom-version">
                {{ item.version }}
              </p>

              <p class="credere-vehicles-showroom-years">
                {{ item.years }}
              </p>
            </span>
          </span>

          <credere-button
            class="credere-vehicles-showroom-button"
            :disabled="replacing"
          >
            Simular financiamento
          </credere-button>
        </component>
      </li>
    </ul>

    <credere-alert
      v-if="!vehicles.length && !replacing"
      class="credere-vehicles-showroom-alert"
    >
      Nenhum veículo encontrado usando os critérios da sua pesquisa.
    </credere-alert>
  </section>
</template>

<style lang="scss">
  $vehicles-showroom-columns-max: 4;
  $vehicles-showroom-columns-min: 1;
  $vehicles-showroom-columns-size: 24rem;

  @function credere-vehicles-showroom-max-width($column) {
    @return $vehicles-showroom-columns-size * ($column + 0.5);
  }

  .credere-vehicles-showroom-list {
    --gap: var(--token-spacing-giga);

    display: grid;
    list-style: none;
    grid-template-columns: repeat($vehicles-showroom-columns-max, 1fr);

    @for $column from $vehicles-showroom-columns-max through $vehicles-showroom-columns-min {
      @container (max-width: #{credere-vehicles-showroom-max-width($column)}) {
        @if $column != $vehicles-showroom-columns-max {
          grid-template-columns: repeat($column, 1fr);
        }

        @if $column == $vehicles-showroom-columns-min {
          --gap: var(--token-spacing-s);
        }
      }
    }
  }

  .credere-vehicles-showroom-link {
    --background-color: var(--token-color-constant-light);
    --border-width: var(--token-border-width-soft);
    --border-style: solid;
    --border-color: var(--token-color-neutral-light);
    --border-radius: var(--token-border-radius-decorative);
    --box-shadow: var(--token-box-shadow-around-2);
    --padding: var(--token-spacing-xs);

    --line-height: var(--token-line-height-s);

    align-items: stretch;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    text-decoration: none;
    overflow: hidden;
    outline: none;
    position: relative;

    .state--replacing & {
      cursor: progress;
    }

    &:hover,
    &:focus,
    &:focus-within {
      --border-color: var(--token-color-neutral-primary);

      .state--replacing & {
        --border-color: var(--token-color-neutral-light);
      }
    }

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --padding: 0;
    }
  }

  .credere-vehicles-showroom-top {
    --gap: var(--token-spacing-giga);

    align-items: stretch;
    display: flex;
    flex-direction: column;
    justify-content: stretch;

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --gap: 0;

      flex-direction: row;

      --border-width: 0 0 var(--token-border-width-soft);
      --border-style: solid;
      --border-color: var(--token-color-neutral-light);
    }
  }

  @keyframes credere-vehicles-showroom-frame-keyframes {
    0%   { background-position: 0 bottom; }
    100% { background-position: 300% bottom; }
  }

  @mixin credere-vehicles-showroom-frame-animation {
    --border-radius: var(--token-border-radius-small);
    --box-shadow: inset 0 0 0 var(--token-border-width-heavy) var(--token-color-constant-light);
    --color: transparent;

    background-repeat: repeat-x;
    background-size: 300% 100%;
    background-image: linear-gradient(
      to right,
      var(--token-color-neutral-lighter),
      var(--token-color-constant-light)
      );

    animation: credere-vehicles-showroom-frame-keyframes 2s linear infinite;
  }

  .credere-vehicles-showroom-frame {
    --background-color: var(--token-color-neutral-lighter);
    --border-color: var(--token-color-neutral-light);
    --border-style: solid;
    --border-width: var(--token-border-width-soft);
    --border-radius: var(--token-border-radius-small);

    aspect-ratio: var(--ar);
    display: block;
    overflow: hidden;
    position: relative;
    width: 100%;
    transition: transform .1s;

    .state--replacing &,
    .state--loading & {
      @include credere-vehicles-showroom-frame-animation;
    }

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --border-width: 0 var(--token-border-width-soft) 0 0;
      --border-radius: 0;

      aspect-ratio: auto;
      width: 8rem;
    }
  }

  .credere-vehicles-showroom-image {
    --border-width: 0;

    display: block;
    object-fit: cover;
    height: 100%;
    mix-blend-mode: darken;
    transition: opacity .1s;
    width: 100%;

    .state--replacing &,
    .state--loading & {
      opacity: 0;
    }
  }

  .credere-vehicles-showroom-info {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    flex: 1;

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --padding: var(--token-spacing-xxxs);
    }
  }

  .credere-vehicles-showroom-name {
    --color: var(--token-color-neutral-dark);
    --font-size: var(--token-font-size-l);
    --font-weight: var(--token-font-weight-bold);

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --font-size: var(--token-font-size-m);
    }

    .state--replacing & {
      @include credere-vehicles-showroom-frame-animation;
    }
  }

  .credere-vehicles-showroom-price {
    --color: var(--token-color-constant-dark);
    --font-size: var(--token-spacing-xl);
    --font-weight: var(--token-font-weight-bold);
    --margin: 0 0 var(--token-spacing-xxxs);

    &:before {
      content: 'R$ ';
      font-size: var(--token-font-size-xl);
    }

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --font-size: var(--token-font-size-xl);

      &:before {
        font-size: var(--token-font-size-l);
      }
    }

    .state--replacing & {
      @include credere-vehicles-showroom-frame-animation;
    }
  }

  .credere-vehicles-showroom-version,
  .credere-vehicles-showroom-years {
    --color: var(--token-color-neutral-dark);
    --font-size: var(--token-font-size-s);
    --line-height: var(--token-line-height-l);

    .state--replacing & {
      @include credere-vehicles-showroom-frame-animation;
    }
  }

  .credere-vehicles-showroom-button {
    --margin: var(--token-spacing-xl) 0 0;

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --margin: var(--token-spacing-s) var(--token-spacing-xs);

      flex: 1;
    }
  }

  .credere-vehicles-showroom-form {
    --margin: 0 0 var(--token-spacing-blaster);

    @container (max-width: #{credere-vehicles-showroom-max-width(1)}) {
      --margin: 0 0 var(--token-spacing-giga);
    }

    // .credere-form-leaves {
    //   grid-template-columns: repeat(5, 1fr);
    // }

    .credere-alert {
      display: none;
    }
  }

  .credere-vehicles-showroom-loading {
    --gap: var(--token-spacing-xxxs);

    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  .credere-vehicles-showroom-alert {
    &:last-child {
      --margin: 0;
    }
  }

  .credere-form-leaf[data-leaf="vehicles[q]"] {
    @container (min-width: 769px) {
      .credere-form-field-input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  .credere-form-leaf[data-leaf="vehicles[sort]"] {
    @container (min-width: 769px) {
      --margin: 0 0 0 calc(var(--token-spacing-s) * -1);

      white-space: nowrap;
      min-width: 28ch;

      .credere-form-field-input {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
</style>
